// 모듈 공통 바탕: 한 모듈 = 큰 지오메트리 하나 = 메시 하나 = 드로우콜 1회.
// 지오메트리는 cap개 슬롯이 각자 정점 구간(verts)을 갖는다. 살아 있는 슬롯은 앞쪽에 모아 두고(죽으면 마지막 슬롯과 자리 교환),
// 매 프레임 살아 있는 슬롯의 정점·제어값을 버퍼에 다시 쓴 뒤 drawRange를 n×슬롯 인덱스 수로 좁힌다.
// 생성 뒤에는 객체를 만들지 않는다(슬롯·버퍼·머티리얼 재사용). 가득 차면 가장 오래된 슬롯을 덮어쓴다.
//
// 정점 속성: aCtl = (진행 0..1, 포락선, 씨앗, 모듈별 값) / aTint = (주색 rgb, 밝기) / aTint2 = (부색 rgb, 모듈별 값)
import * as THREE from 'three/webgpu';
import type { Node } from 'three/webgpu';
import { attribute } from 'three/tsl';
import type { ActionModuleKind, FxModule, FxParams, FxTarget, ModuleKind } from './types';

/** 슬롯 하나의 상태(FxParams 사본 + 진행 + 선형 색). 생성 때 cap개만 만들고 재사용한다 */
export class Slot {
  age = 0;
  life = 1;
  x = 0; y = 0; z = 0; yaw = 0;
  scale = 1; length = 1; width = 1; intensity = 1; seed = 0;
  roll = 0; start = 0; sweep = 0; tail = 0; from = 0; variant = 0; ahead = 0;
  r1 = 1; g1 = 1; b1 = 1; r2 = 1; g2 = 1; b2 = 1;
  /** 따라다니는 대상(없으면 null). 모듈이 onSpawn에서 정한다 */
  tgt: FxTarget | null = null;
  /** 대상이 사라져 끝내기 시작한 시각(age, 초). -1 = 아직 아님 */
  rel = -1;
}

export interface BatchLayout {
  /** 슬롯당 정점 수 */
  verts: number;
  /** 슬롯당 인덱스(슬롯 안 정점 번호 기준) */
  indices: readonly number[];
  /** 슬롯당 uv(정점당 2개) */
  uv: ArrayLike<number>;
}

const tmpColor = new THREE.Color();

/** 정점 속성 노드(머티리얼 작성용) */
export function fxAttributes(): { ctl: Node<'vec4'>; t1: Node<'vec4'>; t2: Node<'vec4'> } {
  // 타입 선언이 속성 노드를 Node<unknown>으로 돌려주므로 실제 타입(vec4)으로 좁힌다(셰이더는 그대로)
  return {
    ctl: attribute('aCtl', 'vec4') as unknown as Node<'vec4'>,
    t1: attribute('aTint', 'vec4') as unknown as Node<'vec4'>,
    t2: attribute('aTint2', 'vec4') as unknown as Node<'vec4'>,
  };
}

export abstract class BatchModule implements FxModule {
  abstract readonly kind: ModuleKind | ActionModuleKind;
  readonly group = new THREE.Group();
  readonly capacity: number;
  protected readonly slots: Slot[] = [];
  protected n = 0;
  protected readonly mesh: THREE.Mesh;
  protected readonly geom = new THREE.BufferGeometry();
  protected readonly pos: Float32Array;
  private readonly ctlArr: Float32Array;
  private readonly t1Arr: Float32Array;
  private readonly t2Arr: Float32Array;
  private readonly attrs: THREE.BufferAttribute[] = [];
  private readonly c4 = new Float32Array(4);
  private readonly idxPer: number;
  private readonly V: number;
  /** 섬광 약화(게임 설정) 같은 전역 밝기 배율 */
  protected gain = 1;
  /** 이 모듈이 지금까지 가득 차서 가장 오래된 것을 대체한 횟수(QA) */
  replaced = 0;

  constructor(cap: number, layout: BatchLayout, material: THREE.Material, renderOrder: number, name: string) {
    this.capacity = cap;
    const V = layout.verts, I = layout.indices.length;
    this.V = V;
    this.idxPer = I;
    this.pos = new Float32Array(cap * V * 3);
    this.ctlArr = new Float32Array(cap * V * 4);
    this.t1Arr = new Float32Array(cap * V * 4);
    this.t2Arr = new Float32Array(cap * V * 4);
    const uvs = new Float32Array(cap * V * 2);
    const idx = new Uint16Array(cap * I);
    for (let s = 0; s < cap; s++) {
      for (let v = 0; v < V * 2; v++) uvs[s * V * 2 + v] = layout.uv[v];
      for (let k = 0; k < I; k++) idx[s * I + k] = layout.indices[k] + s * V;
      this.slots.push(new Slot());
    }
    const mk = (arr: Float32Array, n: number): THREE.BufferAttribute => {
      const a = new THREE.BufferAttribute(arr, n);
      a.setUsage(THREE.DynamicDrawUsage);
      this.attrs.push(a);
      return a;
    };
    this.geom.setAttribute('position', mk(this.pos, 3));
    this.geom.setAttribute('aCtl', mk(this.ctlArr, 4));
    this.geom.setAttribute('aTint', mk(this.t1Arr, 4));
    this.geom.setAttribute('aTint2', mk(this.t2Arr, 4));
    this.geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    this.geom.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geom.setDrawRange(0, 0);
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this.mesh = new THREE.Mesh(this.geom, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.visible = false;
    this.mesh.name = name;
    this.group.name = name;
    this.group.add(this.mesh);
  }

  get activeCount(): number { return this.n; }
  get drawCalls(): number { return this.mesh.visible && this.geom.drawRange.count > 0 ? 1 : 0; }

  setGain(g: number): void { this.gain = g; }

  /** 슬롯 s의 정점 위치를 out[o..]에 쓴다(슬롯당 verts×3) */
  protected abstract positions(s: Slot, out: Float32Array, o: number): void;
  /** 슬롯 s의 제어값 4개를 out에 쓴다: 진행 0..1, 포락선, 씨앗, 모듈별 값 */
  protected abstract ctl(s: Slot, out: Float32Array): void;
  /** aTint2.w: 모듈별 값(UV 스크롤 속도 등) */
  protected tint2w(_s: Slot): number { return 1; }
  /** 슬롯 값을 채운 직후(첫 그리기 전) 호출. 모듈이 대상 같은 추가 값을 슬롯에 붙인다 */
  protected onSpawn(_s: Slot): void { /* 기본: 없음 */ }
  /** 수명이 끝났는지(기본: age >= life). 모듈이 더 일찍 끝낼 수 있다 */
  protected done(s: Slot): boolean { return s.age >= s.life; }

  private init(s: Slot, p: Readonly<FxParams>): void {
    s.age = Math.max(0, Math.min(0.2, p.age0));
    s.life = Math.max(0.05, p.life);
    s.x = p.x; s.y = p.y; s.z = p.z; s.yaw = p.yaw;
    s.scale = p.scale; s.length = p.length; s.width = p.width; s.intensity = p.intensity; s.seed = p.seed;
    s.roll = p.roll; s.start = p.start; s.sweep = p.sweep; s.tail = p.tail; s.from = p.from; s.variant = p.variant; s.ahead = p.ahead;
    tmpColor.setHex(p.color);
    s.r1 = tmpColor.r; s.g1 = tmpColor.g; s.b1 = tmpColor.b;
    tmpColor.setHex(p.color2);
    s.r2 = tmpColor.r; s.g2 = tmpColor.g; s.b2 = tmpColor.b;
    s.tgt = null;
    s.rel = -1;
  }

  private writeSlot(i: number): void {
    const s = this.slots[i], V = this.V, vb = i * V;
    this.positions(s, this.pos, vb * 3);
    const c = this.c4;
    this.ctl(s, c);
    const w2 = this.tint2w(s);
    const bright = s.intensity * this.gain;
    for (let v = 0; v < V; v++) {
      const o = (vb + v) * 4;
      this.ctlArr[o] = c[0]; this.ctlArr[o + 1] = c[1]; this.ctlArr[o + 2] = c[2]; this.ctlArr[o + 3] = c[3];
      this.t1Arr[o] = s.r1; this.t1Arr[o + 1] = s.g1; this.t1Arr[o + 2] = s.b1; this.t1Arr[o + 3] = bright;
      this.t2Arr[o] = s.r2; this.t2Arr[o + 1] = s.g2; this.t2Arr[o + 2] = s.b2; this.t2Arr[o + 3] = w2;
    }
  }

  private flush(): void {
    this.geom.setDrawRange(0, this.n * this.idxPer);
    this.mesh.visible = this.n > 0;
    if (this.n > 0) for (let k = 0; k < this.attrs.length; k++) this.attrs[k].needsUpdate = true;
  }

  spawn(p: Readonly<FxParams>): boolean {
    let i: number;
    if (this.n < this.capacity) {
      i = this.n++;
    } else {
      // 가득 참: 진행이 가장 많이 된 슬롯을 덮어쓴다
      i = 0;
      let best = -1;
      for (let k = 0; k < this.n; k++) {
        const f = this.slots[k].age / this.slots[k].life;
        if (f > best) { best = f; i = k; }
      }
      this.replaced++;
    }
    this.init(this.slots[i], p);
    this.onSpawn(this.slots[i]);
    this.writeSlot(i);
    this.flush();
    return true;
  }

  update(dt: number): void {
    if (this.n === 0) return;
    let i = 0;
    while (i < this.n) {
      const s = this.slots[i];
      s.age += dt;
      if (this.done(s)) {
        const j = --this.n;
        if (i !== j) { this.slots[i] = this.slots[j]; this.slots[j] = s; }
        continue;
      }
      this.writeSlot(i);
      i++;
    }
    this.flush();
  }

  clear(): void {
    this.n = 0;
    this.flush();
  }

  prewarm(on: boolean): void {
    if (on) {
      this.mesh.visible = true;
      this.geom.setDrawRange(0, Math.max(1, this.n) * this.idxPer);
    } else {
      this.flush();
    }
  }

  dispose(): void {
    this.geom.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.group.remove(this.mesh);
    this.n = 0;
  }
}
